import { useState } from "react";
import { loadMeanings, meaningFor, type Meaning } from "../lib/meanings.ts";
import { gameEdition } from "../lib/edition.ts";
import { messages } from "../i18n/index.ts";

interface WordMeaningProps {
  /** Oyunun cevabı. Yalnızca oyun bittikten sonra verilmeli. */
  word: string;
}

type State =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; meaning: Meaning }
  | { kind: "missing" }
  | { kind: "error" };

/**
 * "Ne demek?" — cevabın sözlük karşılığı.
 *
 * Düğmenin arkasında duruyor: anlam dosyası birkaç yüz kilobayt ve ancak
 * soran oyuncu için indiriliyor. Kapalıyken hiçbir ağ isteği yok.
 */
export function WordMeaning({ word }: WordMeaningProps) {
  const [state, setState] = useState<State>({ kind: "idle" });

  async function reveal() {
    if (state.kind === "loading") return;
    setState({ kind: "loading" });
    try {
      const map = await loadMeanings();
      const meaning = meaningFor(map, word);
      setState(meaning ? { kind: "ready", meaning } : { kind: "missing" });
    } catch {
      setState({ kind: "error" });
    }
  }

  if (state.kind === "idle" || state.kind === "loading") {
    return (
      <button
        type="button"
        className="meaning-toggle"
        onClick={() => void reveal()}
        disabled={state.kind === "loading"}
      >
        {state.kind === "loading" ? messages.meaning.loading : messages.meaning.ask(word)}
      </button>
    );
  }

  if (state.kind === "missing") {
    return (
      <p className="meaning-note" role="status">
        {messages.meaning.missing}
      </p>
    );
  }

  if (state.kind === "error") {
    return (
      <button type="button" className="meaning-toggle" onClick={() => void reveal()}>
        {messages.meaning.failed}
      </button>
    );
  }

  return (
    <div className="meaning-card">
      <p className="meaning-card__head">
        <b>{word}</b>
        {state.meaning.partOfSpeech && (
          <span className="meaning-card__tur">{state.meaning.partOfSpeech}</span>
        )}
      </p>
      <p className="meaning-card__text">{state.meaning.definition}</p>
      <p className="meaning-card__source">
        {messages.meaning.source(gameEdition.meaningSource.name)}
      </p>
    </div>
  );
}
